/* ============================================================
   hh-carousel.js — Featured products horizontal carousel (T4)

   Drives the desktop slide track of the featured-products section
   (hh-product-carousel.liquid), keyed to [data-hh-carousel]. Ports the
   desktop logic of Home.jsx CollectionSection (15-96):

     • measure the viewport's natural width, snap to device pixels
     • slotPx = (width + PEEK*GAP) / (cols + PEEK)   (PEEK 0.25, GAP 48)
     • each slide width = slotPx - GAP
     • translate the track by -step*slotPx on chevron click
     • disable chevrons at the bounds (step 0 .. maxStep = slides - cols)
     • recompute on resize (debounced via rAF)

   Mobile (< 768px) is pure CSS scroll-snap — JS does nothing there.
   Theme-editor friendly: queries are scoped to the section root, state is
   per-instance, multiple instances are safe, and it re-inits on
   shopify:section:load.
   ============================================================ */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-hh-carousel]';
  var PEEK = 0.25; // fraction of the next card that peeks at the right edge
  var GAP = 48;    // --space-7, matches the CSS track gap
  var MOBILE_MAX = 767;

  // Per-instance teardown handlers, so re-init / unload can detach cleanly.
  var instances = []; // { root, destroy }

  function destroyFor(root) {
    for (var i = instances.length - 1; i >= 0; i--) {
      if (instances[i].root === root) {
        try { instances[i].destroy(); } catch (e) { /* no-op */ }
        instances.splice(i, 1);
      }
    }
  }

  function init(sectionEl) {
    if (!sectionEl) return;
    var root = sectionEl.matches(ROOT_SELECTOR)
      ? sectionEl
      : sectionEl.querySelector(ROOT_SELECTOR);
    if (!root) return; // no carousel in this section — no-op

    // Re-init safety: tear down any prior instance bound to this root.
    destroyFor(root);

    var track = root.querySelector('.hh-carousel__track');
    if (!track) return;
    var slides = track.querySelectorAll('.hh-carousel__slide');
    if (!slides.length) return;

    var cols = parseInt(root.getAttribute('data-cols'), 10);
    if (!cols || cols < 1) cols = 3;

    // Chevrons live in the section head, a sibling of the viewport root.
    var section = root.closest('.hh-carousel-shell') || document;
    var prevBtn = section.querySelector('.hh-chev[data-dir="left"]');
    var nextBtn = section.querySelector('.hh-chev[data-dir="right"]');

    var step = 0;
    var slotPx = null;
    var maxStep = Math.max(0, slides.length - cols);

    function setChevState() {
      var canPrev = step > 0;
      var canNext = step < maxStep;
      if (prevBtn) {
        prevBtn.disabled = !canPrev;
        prevBtn.classList.toggle('is-disabled', !canPrev);
      }
      if (nextBtn) {
        nextBtn.disabled = !canNext;
        nextBtn.classList.toggle('is-disabled', !canNext);
      }
    }

    function applyTransform() {
      track.style.transform = slotPx != null
        ? 'translateX(' + (-step * slotPx) + 'px)'
        : 'translateX(0)';
    }

    // Desktop: measure the viewport, snap to device pixels, derive slot width
    // and set each slide's width. Mobile: clear inline styles (CSS owns it).
    function measure() {
      var isMobile = window.innerWidth <= MOBILE_MAX;

      if (isMobile) {
        slotPx = null;
        track.style.transform = '';
        for (var m = 0; m < slides.length; m++) slides[m].style.width = '';
        return;
      }

      var dpr = window.devicePixelRatio || 1;
      var natural = root.getBoundingClientRect().width;
      var snapped = Math.round(natural * dpr) / dpr;
      slotPx = (snapped + PEEK * GAP) / (cols + PEEK);

      var w = (slotPx - GAP) + 'px';
      for (var i = 0; i < slides.length; i++) slides[i].style.width = w;

      // Re-clamp step in case cols/slide-count math shifted the bound.
      maxStep = Math.max(0, slides.length - cols);
      if (step > maxStep) step = maxStep;

      applyTransform();
      setChevState();
    }

    function go(delta) {
      var next = step + delta;
      if (next < 0) next = 0;
      if (next > maxStep) next = maxStep;
      if (next === step) return;
      step = next;
      applyTransform();
      setChevState();
    }

    function onPrev() { if (window.innerWidth > MOBILE_MAX) go(-1); }
    function onNext() { if (window.innerWidth > MOBILE_MAX) go(1); }

    if (prevBtn) prevBtn.addEventListener('click', onPrev);
    if (nextBtn) nextBtn.addEventListener('click', onNext);

    // Debounce resize via rAF so we never thrash layout mid-frame.
    var rafId = null;
    function onResize() {
      if (rafId) return;
      rafId = window.requestAnimationFrame(function () {
        rafId = null;
        measure();
      });
    }
    window.addEventListener('resize', onResize);

    // Initial measure: defer to a frame so the section is laid out, plus a
    // load fallback for late-loading webfonts/images. Never sets section height.
    window.requestAnimationFrame(measure);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', measure, { once: true });
    }

    function destroy() {
      if (rafId) { window.cancelAnimationFrame(rafId); rafId = null; }
      window.removeEventListener('resize', onResize);
      if (prevBtn) prevBtn.removeEventListener('click', onPrev);
      if (nextBtn) nextBtn.removeEventListener('click', onNext);
      track.style.transform = '';
      for (var i = 0; i < slides.length; i++) slides[i].style.width = '';
    }

    instances.push({ root: root, destroy: destroy });

    // First paint of the disabled/enabled state (cheap; measure repeats it).
    setChevState();
  }

  function initAll(scope) {
    var ctx = scope || document;
    var roots = ctx.querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target && e.target.querySelector
      ? e.target.querySelector(ROOT_SELECTOR)
      : null;
    if (root) destroyFor(root);
  });
})();
